import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Button, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/**
 * App-level auth gate. Checks if the user has a valid session by hitting
 * /health (always allowed) then a protected endpoint. If 401, shows a
 * CRT-themed password form that POSTs to /login on the API.
 *
 * On success the API sets an httpOnly cookie — subsequent requests
 * include it automatically via credentials: 'include'.
 */
interface Props {
  apiUrl: string;
  children: React.ReactNode;
}

const LoginGate: React.FC<Props> = ({ apiUrl, children }) => {
  const [authed, setAuthed] = useState<boolean | null>(null); // null = checking
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check session on mount
  useEffect(() => {
    fetch(`${apiUrl}/api/v1/youtube/summary`, { credentials: 'include' })
      .then((r) => {
        if (r.ok) setAuthed(true);
        else setAuthed(false);
      })
      .catch(() => setAuthed(false));
  }, [apiUrl]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${apiUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `password=${encodeURIComponent(password)}`,
        credentials: 'include',
        redirect: 'manual', // Don't follow the 302
      });

      if (resp.status === 200 || resp.status === 302 || resp.type === 'opaqueredirect') {
        // Session cookie set — reload to pick it up
        window.location.reload();
      } else {
        setError('ACCESS DENIED');
        setPassword('');
      }
    } catch (err) {
      setError('CONNECTION ERROR');
    } finally {
      setLoading(false);
    }
  };

  // Still checking
  if (authed === null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' }}>
        <Typography sx={{ fontFamily: "'VT323', monospace", fontSize: '20px', color: '#00ff41' }}>
          &gt; AUTHENTICATING...
        </Typography>
      </Box>
    );
  }

  // Authenticated — render app
  if (authed) {
    return <>{children}</>;
  }

  // Not authenticated — login form
  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0a0a0a',
    }}>
      <Box sx={{
        backgroundColor: '#0f0f0f',
        border: '1px solid #1e3a1e',
        p: 5,
        maxWidth: 420,
        width: '100%',
        position: 'relative',
        '&::before': {
          content: '"// AUTOPOD :: AUTHENTICATION //"',
          position: 'absolute',
          top: '-9px',
          left: '14px',
          background: '#0a0a0a',
          px: '8px',
          fontFamily: "'Press Start 2P', monospace",
          fontSize: '8px',
          color: '#5aaa5a',
          letterSpacing: '0.1em',
        },
      }}>
        <Box sx={{
          mb: 3,
          pl: '12px',
          borderLeft: '2px solid #00ff41',
          fontFamily: "'Press Start 2P', monospace",
          fontSize: '10px',
          color: '#5aaa5a',
          letterSpacing: '0.1em',
        }}>
          &gt; ENTER ACCESS CODE
        </Box>

        {error && (
          <Typography sx={{
            color: '#ff3344',
            fontFamily: "'VT323', monospace",
            fontSize: '18px',
            mb: 2,
            textShadow: '0 0 6px rgba(255,51,68,0.5)',
          }}>
            &gt; {error}
          </Typography>
        )}

        <form onSubmit={handleLogin}>
          <TextField
            type={showPassword ? 'text' : 'password'}
            fullWidth
            autoFocus
            placeholder="&gt; password_"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ autoComplete: 'current-password' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    tabIndex={-1}
                    sx={{ color: '#5aaa5a', '&:hover': { color: '#00ff41' } }}
                    aria-label={showPassword ? 'hide password' : 'show password'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="outlined"
            disabled={loading || !password}
          >
            {loading ? '[ AUTHENTICATING... ]' : '[ AUTHENTICATE ]'}
          </Button>
        </form>

        <Typography sx={{
          mt: 3,
          fontFamily: "'VT323', monospace",
          fontSize: '16px',
          color: '#5aaa5a',
        }}>
          &gt; SYSTEM STATUS: <span style={{ color: '#00ff41', textShadow: '0 0 6px rgba(0,255,65,0.8)' }}>LOCKED</span>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginGate;
